import React, { useState } from 'react'
import styled from 'styled-components'
import Colors from '../assets/styles/Colors'
import Responsive from '../assets/config/Responsive'
import { Container } from '../assets/styles/GlobalStyle'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../store'
import { AppState } from '../store/appSlice'
import LogoGe from '../assets/icons/JSX/LogoGe'
import Logo from '../assets/icons/JSX/Logo'

export interface PageInfo {
  route: string
  title: string
}

const PageHeader: React.FC<{
  total?: number
  passed?: number
  pages?: PageInfo[]
}> = ({ total = 0, passed = 0, pages = [] }) => {
  const navigate = useNavigate()
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const handleLineClick = (index: number) => {
    if (index < passed && pages[index]) {
      navigate(pages[index].route)
    }
  }

  return (
    <Wrapper>
      <Container>
        <HeaderRow>
          {lang === 'ge' ? (
            <LogoGeStyle onClick={() => navigate('/')} />
          ) : lang === 'en' ? (
            <LogoEnStyle onClick={() => navigate('/')} />
          ) : (
            <LogoEnStyle onClick={() => navigate('/')} />
          )}
          <Block>
            {total > 0 && (
              <Progress>
                {Array.from({ length: total }).map((_, index) => {
                  const isPassed = index < passed
                  const pageInfo = pages[index]
                  const isHovered = hoveredIndex === index

                  return (
                    <LineWrapper key={pageInfo?.route || `page-${index}`}>
                      <Line
                        active={isPassed}
                        clickable={isPassed && !!pageInfo}
                        onMouseEnter={() => isPassed && pageInfo && setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => handleLineClick(index)}
                      >
                        {isHovered && isPassed && pageInfo && (
                          <HoverText>
                            <span>{pageInfo.title}</span> <Triangle />
                          </HoverText>
                        )}
                      </Line>
                    </LineWrapper>
                  )
                })}
              </Progress>
            )}

            <Counts>
              {passed && (
                <Passed>
                  {passed <= 9 ? 0 : null}
                  {passed}
                </Passed>
              )}
              {total && (
                <Total>
                  <span>/</span>
                  {total <= 9 ? 0 : null}
                  {total}
                </Total>
              )}
            </Counts>
          </Block>
        </HeaderRow>
      </Container>
    </Wrapper>
  )
}

const LineWrapper = styled.div`
  position: relative;
`

const Line = styled.div<{ active: boolean; clickable: boolean }>`
  width: 30px;
  height: 4px;
  background-color: ${(props) => (props.active ? Colors.primaryRed : Colors.secondaryGrey)};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: opacity 0.2s ease;
  position: relative;

  &:hover {
    opacity: ${(props) => (props.clickable ? 0.8 : 1)};
  }
`

const Triangle = styled.div`
  width: 0px;
  height: 0px;
  border-width: 9px 8px 8px;
  border-style: solid;
  border-color: ${Colors.white} transparent transparent;
  border-image: initial;
  position: absolute;
  top: -16px;
  transform: rotate(180deg);
`

const HoverText = styled.div`
  user-select: none;
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  white-space: nowrap;
  height: 40px;
  border-radius: 4px;
  background-color: ${Colors.white};
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  padding-inline: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${Colors.nevadaGrey};
  z-index: 5;
`

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;

  ${Responsive.mobile} {
    display: none;
  }
`

const Passed = styled.span`
  font-size: 18px;
  color: ${Colors.primaryBlack};
`
const Total = styled.span`
  font-size: 14px;
  color: ${Colors.nevadaGrey};

  span {
    margin-inline: 5px;
    font-size: 16px;
  }
`
const Counts = styled.div``

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const Block = styled.div`
  display: flex;
  align-items: center;
`

const LogoGeStyle = styled(LogoGe)`
  width: 90px;
  height: 40px;
  cursor: pointer;
  user-select: none;

  ${Responsive.mobile} {
    width: 64px;
    height: 28px;
  }
`
const LogoEnStyle = styled(Logo)`
  width: 90px;
  height: 40px;
  cursor: pointer;
  user-select: none;

  ${Responsive.mobile} {
    width: 64px;
    height: 28px;
  }
`

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 88px;
  background-color: ${Colors.white};
  display: flex;
  align-items: center;
  user-select: none;
  z-index: 5;
  -webkit-box-shadow: 0px 0px 15px 3px #88888811;
  box-shadow: 0px 0px 15px 3px #88888811;
  padding-left: 100px;
  padding-right: 100px;

  ${Responsive.mobile} {
    padding-left: 0px;
    padding-right: 0px;
    height: 64px;
  }
`
export default PageHeader
